

import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, push, update, onValue, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const PayableForm = ({ transactionToEdit, onSuccess, onCancel, initialSupplier }) => {
  const [voucherNo, setVoucherNo] = useState('');
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().split('T')[0]);
  const [supplier, setSupplier] = useState(initialSupplier || '');
  const [supplierAddress, setSupplierAddress] = useState('');
  const [supplierCity, setSupplierCity] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [mrnChecked, setMrnChecked] = useState(false);
  const [mrnNo, setMrnNo] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [paymentBy, setPaymentBy] = useState('');
  const [bankCharge, setBankCharge] = useState('');
  const [bankName, setBankName] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [chequeDt, setChequeDt] = useState('');
  const [remarks, setRemarks] = useState('');
  const [message, setMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentAmountInput, setPaymentAmountInput] = useState('');
  const [netAmountInput, setNetAmountInput] = useState('');
  const [originalNetTotal, setOriginalNetTotal] = useState(0);
  const [currentOutstanding, setCurrentOutstanding] = useState(0);
  const [isFetchingInvoices, setIsFetchingInvoices] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [allBills, setAllBills] = useState(true);
  const [selectedBill, setSelectedBill] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [billNumbers, setBillNumbers] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Fetch payment modes
  useEffect(() => {
    if (currentUser) {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const paymentModesRef = ref(database, `users/${encodedEmail}/paymentModes`);
      onValue(
        paymentModesRef,
        (snapshot) => {
          const data = snapshot.val();
          const modesList = data
            ? Object.keys(data).map((key) => ({
                id: key,
                code: data[key].code,
                description: data[key].description,
              }))
            : [];
          setPaymentModes(modesList);
        },
        (error) => {
          console.error('Error fetching payment modes:', error);
          setMessage('Failed to fetch payment modes.');
        }
      );
    }
  }, [currentUser]);

  // Fetch current user, max Voucher No, and suppliers
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        const encodedEmail = encodeEmailForPath(user.email);
        const transactionsRef = ref(database, `users/${encodedEmail}/transactions`);
        get(transactionsRef)
          .then((snapshot) => {
            let maxVoucherNo = 0;
            if (snapshot.exists()) {
              const transactions = snapshot.val();
              const voucherNos = Object.values(transactions)
                .map((transaction) => parseInt(transaction.voucherNo, 10))
                .filter((num) => !isNaN(num));
              maxVoucherNo = voucherNos.length > 0 ? Math.max(...voucherNos) : 0;
            }
            if (!transactionToEdit) {
              setVoucherNo((maxVoucherNo + 1).toString());
            }
          })
          .catch((error) => {
            console.error('Error fetching Voucher No:', error);
            setVoucherNo('1');
          });

        const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
        onValue(
          suppliersRef,
          (snapshot) => {
            const data = snapshot.val();
            const supplierList = data
              ? Object.keys(data).map((key) => ({
                  id: key,
                  supplierName: data[key].supplierName || '',
                  address: data[key].address || '',
                  city: data[key].city || '',
                  zipCode: data[key].zipCode || '',
                }))
              : [];
            setSuppliers(supplierList);
          },
          (error) => {
            console.error('Error fetching suppliers:', error);
            setMessage('Failed to fetch suppliers.');
          }
        );
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, [transactionToEdit]);

  // Fetch invoices
  useEffect(() => {
    if (currentUser && supplier) {
      setIsFetchingInvoices(true);
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
      const unsubscribe = onValue(
        invoicesRef,
        (snapshot) => {
          const data = snapshot.val();
          let invoiceList = [];
          if (data) {
            invoiceList = Object.keys(data)
              .map((key) => ({
                id: key,
                ...data[key],
              }))
              .filter(
                (invoice) =>
                  invoice.supplierDetails?.supplier.trim().toLowerCase() === supplier.trim().toLowerCase() &&
                  (!invoice.summary?.paymentStatus || invoice.summary.paymentStatus !== 'Complete')
              );
          }
          setInvoices(invoiceList);
          setBillNumbers(invoiceList.map((inv) => inv.supplierDetails?.invoiceNo || '').filter((no) => no));
          setIsFetchingInvoices(false);
        },
        (error) => {
          console.error('Error fetching invoices:', error);
          setMessage('Failed to fetch invoices.');
          setIsFetchingInvoices(false);
        }
      );

      return () => unsubscribe();
    } else {
      setInvoices([]);
      setBillNumbers([]);
      setTransactions([]);
      setOriginalNetTotal(0);
      setCurrentOutstanding(0);
      setNetAmountInput('');
      setTotalAmount('');
      setIsFetchingInvoices(false);
    }
  }, [currentUser, supplier]);

  // Set transactions based on selection
  useEffect(() => {
    if (transactionToEdit) return; // Don't overwrite during edit

    let selectedInvoices = [];
    if (allBills) {
      selectedInvoices = invoices;
    } else if (selectedBill) {
      const inv = invoices.find((i) => i.supplierDetails?.invoiceNo === selectedBill);
      if (inv) selectedInvoices = [inv];
    }

    if (selectedInvoices.length > 0) {
      let sno = 0;
      const newTransactions = selectedInvoices.flatMap((invoice) => {
        const supplierDetails = invoice.supplierDetails || {};
        const items = invoice.items || [];
        const summary = invoice.summary || {};

        return items.map((item) => {
          sno++;
          const netValue = Number(item.netValue || 0);
          const itemProportion = netValue / Number(summary.netAmt || netValue || 1);
          const itemPaid = Number(summary.totalPaid || 0) * itemProportion;
          const balance = Math.max(0, netValue - itemPaid);

          return {
            sno,
            transactionType: 'Purchase',
            remarks: '',
            mrnNo: supplierDetails.mrnNo || '',
            mrnDt: supplierDetails.invoiceDate || '',
            invNo: supplierDetails.invoiceNo || '',
            invDt: supplierDetails.invoiceDate || '',
            item: item.item || '',
            batch: item.batch || '',
            qty: item.qty || 0,
            unitPrice: item.unitPrice ? Number(item.unitPrice).toFixed(2) : '0.00',
            netValue: netValue.toFixed(2),
            paidAmt: itemPaid.toFixed(2),
            adjAmt: '0.00',
            balance: balance.toFixed(2),
            paymentAmt: '',
            invoiceId: invoice.id,
            itemId: item.id || '',
          };
        });
      });

      setTransactions(newTransactions);
      const originalTotal = newTransactions.reduce((sum, t) => sum + Number(t.netValue || 0), 0);
      setOriginalNetTotal(originalTotal);
      const outstanding = newTransactions.reduce((sum, t) => sum + Number(t.balance || 0), 0);
      setCurrentOutstanding(outstanding);
      setNetAmountInput(outstanding.toFixed(2));
      setTotalAmount(originalTotal.toFixed(2));
    } else {
      setTransactions([]);
      setOriginalNetTotal(0);
      setCurrentOutstanding(0);
      setNetAmountInput('');
      setTotalAmount('');
    }
  }, [invoices, allBills, selectedBill, transactionToEdit]);

  // Set allBills and selectedBill for editing
  useEffect(() => {
    if (transactionToEdit && invoices.length > 0 && billNumbers.length > 0 && transactions.length > 0) {
      const uniqueInvNos = new Set(transactions.map((t) => t.invNo));
      const allInvNos = new Set(billNumbers);
      const isAll = uniqueInvNos.size === allInvNos.size && [...uniqueInvNos].every((no) => allInvNos.has(no));
      setAllBills(isAll);
      if (!isAll && uniqueInvNos.size === 1) {
        setSelectedBill([...uniqueInvNos][0]);
      }
    }
  }, [transactionToEdit, invoices, billNumbers, transactions]);

  // Update net amount
  useEffect(() => {
    const paymentAmount = Number(paymentAmountInput) || 0;
    const newNetAmount = Math.max(0, currentOutstanding - paymentAmount);
    setNetAmountInput(newNetAmount.toFixed(2));
  }, [paymentAmountInput, currentOutstanding]);

  // Initialize form for editing
  useEffect(() => {
    if (transactionToEdit) {
      setVoucherNo(transactionToEdit.voucherNo || '');
      setVoucherDate(transactionToEdit.voucherDate || new Date().toISOString().split('T')[0]);
      setSupplier(transactionToEdit.supplier || '');
      setMrnChecked(transactionToEdit.mrnChecked || false);
      setMrnNo(transactionToEdit.mrnNo || '');
      setTotalAmount(transactionToEdit.totalAmount || '');

      if (transactionToEdit.transactions?.length > 0) {
        const editedTransactions = transactionToEdit.transactions.map((t, index) => ({
          sno: index + 1,
          transactionType: t.transactionType || 'Purchase',
          remarks: t.remarks || '',
          mrnNo: t.mrnNo || '',
          mrnDt: t.mrnDt || '',
          invNo: t.invNo || '',
          invDt: t.invDt || '',
          item: t.item || '',
          batch: t.batch || '',
          qty: t.qty || 0,
          unitPrice: t.unitPrice || '0.00',
          netValue: t.netValue || '0.00',
          paidAmt: t.paidAmt || '0.00',
          adjAmt: t.adjAmt || '0.00',
          balance: t.balance || '0.00',
          paymentAmt: t.paymentAmt || '',
          invoiceId: t.invoiceId || '',
          itemId: t.itemId || '',
        }));
        setTransactions(editedTransactions);
        const total = editedTransactions.reduce((sum, t) => sum + Number(t.netValue || 0), 0);
        setOriginalNetTotal(total);
        setCurrentOutstanding(Number(transactionToEdit.netAmount || total));
        setNetAmountInput(Number(transactionToEdit.netAmount || total).toFixed(2));
        setPaymentAmountInput(transactionToEdit.paymentAmount || '');
      }

      setPaymentBy(transactionToEdit.paymentBy || '');
      setBankCharge(transactionToEdit.bankCharge || '');
      setBankName(transactionToEdit.bankName || '');
      setChequeNo(transactionToEdit.chequeNo || '');
      setChequeDt(transactionToEdit.chequeDt || '');
      setRemarks(transactionToEdit.remarks || '');

      const selectedSupplier = suppliers.find((s) => s.supplierName === transactionToEdit.supplier);
      if (selectedSupplier) {
        setSupplierAddress(selectedSupplier.address || '');
        setSupplierCity(selectedSupplier.city || '');
      }
    } else {
      setSupplier(initialSupplier || '');
      setSupplierAddress('');
      setSupplierCity('');
      setVoucherDate(new Date().toISOString().split('T')[0]);
    }
  }, [transactionToEdit, suppliers, initialSupplier]);

  // Update supplier address and city
  useEffect(() => {
    const selectedSupplier = suppliers.find((s) => s.supplierName === supplier);
    if (selectedSupplier) {
      setSupplierAddress(selectedSupplier.address || '');
      setSupplierCity(`${selectedSupplier.city || ''} ${selectedSupplier.zipCode || ''}`.trim());
    } else {
      setSupplierAddress('');
      setSupplierCity('');
    }
  }, [supplier, suppliers]);

  const handleMrnNoChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Allow only digits
    if (value.length <= 18) {
      setMrnNo(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!voucherNo || !voucherDate || !supplier || !paymentBy) {
      setMessage('Please fill in all required fields (Voucher No, Date, Supplier, and Payment Method).');
      return;
    }
    if (mrnChecked && mrnNo.length !== 18) {
      setMessage('MRN Number must be exactly 18 digits.');
      return;
    }
    if (transactions.length === 0 || transactions.every((t) => !t.invNo || !t.item)) {
      setMessage('Please ensure at least one valid invoice item is present.');
      return;
    }
    if (!currentUser) {
      setMessage('You must be logged in to save a transaction.');
      return;
    }
    const paymentAmount = Number(paymentAmountInput) || 0;
    if (paymentAmount > currentOutstanding) {
      setMessage('Payment amount cannot exceed the current outstanding amount.');
      return;
    }
    const newOutstanding = Math.max(0, currentOutstanding - paymentAmount);
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const transactionData = {
        voucherNo,
        voucherDate,
        supplier,
        supplierAddress,
        supplierCity,
        mrnChecked,
        mrnNo,
        totalAmount: originalNetTotal.toFixed(2),
        transactions,
        paymentBy,
        bankCharge,
        bankName,
        chequeNo,
        chequeDt,
        remarks,
        netAmount: newOutstanding.toFixed(2),
        paymentAmount: paymentAmount.toFixed(2),
        originalAmount: originalNetTotal.toFixed(2),
        outstandingBefore: currentOutstanding.toFixed(2),
        timestamp: new Date().toISOString(),
        createdBy: currentUser.email,
      };
      if (transactionToEdit && transactionToEdit.id) {
        // UPDATE scenario
        const transactionRef = ref(database, `users/${encodedEmail}/transactions/${transactionToEdit.id}`);
        await update(transactionRef, transactionData);
        alert('Payment Updated Successfully.');
        setMessage('');
      } else {
        // SAVE scenario
        const transactionsRef = ref(database, `users/${encodedEmail}/transactions`);
        await push(transactionsRef, transactionData);
        alert('Payment Saved Successfully.');
        setMessage('');
      }
      if (transactions.length > 0) {
        const invoiceIds = [...new Set(transactions.map((t) => t.invoiceId))];
        for (const invoiceId of invoiceIds) {
          const invoiceRef = ref(database, `users/${encodedEmail}/invoices/${invoiceId}`);
          const invoiceTransactions = transactions.filter((t) => t.invoiceId === invoiceId);
          const invoiceTotal = invoiceTransactions.reduce((sum, t) => sum + Number(t.netValue || 0), 0);
          const invoiceOutstanding = invoiceTransactions.reduce((sum, t) => sum + Number(t.balance || 0), 0);
          const allocated = currentOutstanding > 0 ? (invoiceOutstanding / currentOutstanding) * paymentAmount : 0;
          const currentPaid = invoiceTransactions.reduce((sum, t) => sum + Number(t.paidAmt || 0), 0);
          const invoicePaid = currentPaid + allocated;
          const newInvoiceOutstanding = Math.max(0, invoiceTotal - invoicePaid);
          const paymentStatus = newInvoiceOutstanding <= 0.01 ? 'Complete' : 'Pending';
          await update(invoiceRef, {
            'summary/netAmt': invoiceTotal.toFixed(2),
            'summary/outstanding': newInvoiceOutstanding.toFixed(2),
            'summary/paymentStatus': paymentStatus,
            'summary/lastPaymentDate': new Date().toISOString(),
            'summary/totalPaid': invoicePaid.toFixed(2),
          });
        }
      }
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error saving transaction:', error);
      setMessage(`Error saving transaction: ${error.message}`);
    }
  };

  const resetForm = () => {
    setVoucherNo('');
    setVoucherDate(new Date().toISOString().split('T')[0]);
    setSupplier(initialSupplier || '');
    setSupplierAddress('');
    setSupplierCity('');
    setMrnChecked(false);
    setMrnNo('');
    setTotalAmount('');
    setTransactions([]);
    setPaymentBy('');
    setBankCharge('');
    setBankName('');
    setChequeNo('');
    setChequeDt('');
    setRemarks('');
    setPaymentAmountInput('');
    setNetAmountInput('');
    setOriginalNetTotal(0);
    setCurrentOutstanding(0);
    setAllBills(true);
    setSelectedBill('');
  };

  const handleCancel = () => {
    resetForm();
    setMessage('');
    onCancel();
  };

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '1rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '1.5rem',
      margin: '0 auto',
      maxWidth: '1200px',
      overflowX: 'auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#2563eb',
    },
    headerButtons: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    backButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      backgroundColor: '#6b7280',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
      transform: 'translateY(0)',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.25rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      overflowY: 'auto',
    },
    sectionHeading: {
      fontSize: '1.1rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '0.75rem',
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    input: {
      display: 'block',
      width: '80%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    checkbox: {
      width: '14px',
      height: '14px',
      margin: '0',
      cursor: 'pointer',
    },
    tableContainer: {
      marginTop: '1rem',
      overflowX: 'auto',
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      position: 'relative',
      maxWidth: '100%',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(241,245,249,0.95) 100%)',
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.75rem',
      minWidth: '1000px',
    },
    tableHeader: {
      padding: '0.5rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
      whiteSpace: 'nowrap',
    },
    tableCell: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    tableInput: {
      border: '1px solid #d1d5db',
      padding: '0.3rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.75rem',
      boxSizing: 'border-box',
      width: '100%',
      height: '28px',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    remarksInput: {
      border: '1px solid #d1d5db',
      padding: '0.3rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.75rem',
      boxSizing: 'border-box',
      width: '200px',
      height: '28px',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    summaryContainer: {
      marginTop: '1.5rem',
      width: '100%',
      maxWidth: '250px',
      marginLeft: 'auto',
    },
    summaryBox: {
      border: '1px solid #e2e8f0',
      padding: '1rem',
      borderRadius: '8px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.5rem',
      fontSize: '0.8rem',
      color: '#374151',
    },
    summaryInput: {
      border: '1px solid #d1d5db',
      padding: '0.4rem',
      width: '40%',
      borderRadius: '4px',
      fontSize: '0.8rem',
      textAlign: 'right',
      backgroundColor: '#f3f4f6',
    },
    summaryTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      fontWeight: '600',
      borderTop: '1px solid #e2e8f0',
      paddingTop: '0.5rem',
      marginTop: '0.75rem',
      color: '#1f2937',
    },
    actionButtons: {
      marginTop: '1.5rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      paddingTop: '0.75rem',
      borderTop: '1px solid #e2e8f0',
      flexWrap: 'wrap',
    },
    saveButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    cancelButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    message: {
      padding: '0.75rem',
      borderRadius: '6px',
      marginBottom: '1rem',
      fontSize: '0.8rem',
      textAlign: 'center',
    },
    textarea: {
      border: '1px solid #d1d5db',
      padding: '0.5rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.8rem',
      width: '80%',
      height: '60px',
      resize: 'none',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
  };

  if (loading || !currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#718096', fontSize: '1rem' }}>
          <h2>Please login to access the Payable Form</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            {transactionToEdit ? 'Edit Payment Transaction' : 'Add Payment Transaction'}
          </div>
          <div style={styles.headerButtons}>
            <button
              onClick={handleCancel}
              style={styles.backButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(107, 114, 128, 0.3)';
              }}
            >
              ← Back
            </button>
          </div>
        </div>

        {message && (
          <div
            style={{
              ...styles.message,
              backgroundColor: message.includes('Error') ? '#fee2e2' : '#d1fae5',
              color: message.includes('Error') ? '#b91c1c' : '#065f46',
            }}
          >
            {message}
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionHeading}>📋 Transaction Details</h2>
          <div style={styles.gridContainer}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Voucher No*</label>
              <input
                type="text"
                value={voucherNo}
                readOnly
                style={{ ...styles.input, backgroundColor: '#f3f4f6' }}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Date*</label>
              <input
                type="date"
                value={voucherDate}
                onChange={(e) => setVoucherDate(e.target.value)}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Supplier*</label>
              <select
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                style={styles.select}
                required
              >
                <option value="">Select Supplier</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.supplierName}>
                    {s.supplierName}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Supplier Address</label>
              <input
                type="text"
                value={supplierAddress}
                readOnly
                style={{ ...styles.input, backgroundColor: '#f3f4f6' }}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Supplier City</label>
              <input
                type="text"
                value={supplierCity}
                readOnly
                style={{ ...styles.input, backgroundColor: '#f3f4f6' }}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>All MRN</label>
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  checked={allBills}
                  onChange={(e) => setAllBills(e.target.checked)}
                  style={styles.checkbox}
                  disabled={!!transactionToEdit}
                />
              </div>
            </div>
            {!allBills && (
              <div style={styles.formGroup}>
                <label style={styles.label}>MRN No</label>
                <select
                  value={selectedBill}
                  onChange={(e) => setSelectedBill(e.target.value)}
                  style={styles.select}
                  disabled={!!transactionToEdit}
                >
                  <option value="">Select Bill Number</option>
                  {billNumbers.map((no, index) => (
                    <option key={index} value={no}>
                      {no}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
          </div>
        </div>

        {isFetchingInvoices && (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#718096' }}>
            Loading invoices...
          </div>
        )}

        {transactions.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>📦 Transactions</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, width: '60px' }}>S.No</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>Type</th>
                    <th style={{ ...styles.tableHeader, width: '200px' }}>Remarks</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>MRN No</th>
                    <th style={{ ...styles.tableHeader, width: '120px' }}>MRN Dt</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>Inv No</th>
                    <th style={{ ...styles.tableHeader, width: '120px' }}>Inv Dt</th>
                    <th style={{ ...styles.tableHeader, width: '200px' }}>Item</th>

                    <th style={{ ...styles.tableHeader, width: '70px' }}>Qty</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>Unit Price</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>Net Amount</th>
                    {/* <th style={{ ...styles.tableHeader, width: '100px' }}>Paid Amount</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>Balance</th> */}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <td style={{ ...styles.tableCell, fontWeight: '600', color: '#2563eb', width: '60px' }}>
                        {transaction.sno}
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>{transaction.transactionType}</td>
                      <td style={{ ...styles.tableCell, width: '200px' }}>
                        <input
                          type="text"
                          value={transaction.remarks}
                          onChange={(e) => {
                            const newTransactions = [...transactions];
                            newTransactions[index].remarks = e.target.value;
                            setTransactions(newTransactions);
                          }}
                          style={styles.remarksInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>{transaction.mrnNo}</td>
                      <td style={{ ...styles.tableCell, width: '120px' }}>
                        <input
                          type="date"
                          value={transaction.invDt}
                          onChange={(e) => {
                            const newTransactions = [...transactions];
                            newTransactions[index].invDt = e.target.value;
                            setTransactions(newTransactions);
                          }}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>{transaction.invNo}</td>
                      <td style={{ ...styles.tableCell, width: '120px' }}>
                        <input
                          type="date"
                          value={transaction.invDt}
                          onChange={(e) => {
                            const newTransactions = [...transactions];
                            newTransactions[index].invDt = e.target.value;
                            setTransactions(newTransactions);
                          }}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '200px' }}>{transaction.item}</td>

                      <td style={{ ...styles.tableCell, width: '70px' }}>{transaction.qty}</td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>{transaction.unitPrice}</td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>{transaction.netValue}</td>
                      {/* <td style={{ ...styles.tableCell, width: '100px' }}>{transaction.paidAmt}</td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>{transaction.balance}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionHeading}>💳 Payment Details</h2>
          <div style={styles.gridContainer}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Payment By*</label>
              <select
                value={paymentBy}
                onChange={(e) => setPaymentBy(e.target.value)}
                style={styles.select}
                required
              >
                <option value="">Select Payment Method</option>
                {paymentModes.map((mode) => (
                  <option key={mode.id} value={mode.code}>
                    {mode.code}
                  </option>
                ))}
              </select>
            </div>
            {paymentBy === 'Cheque' && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bank Charge</label>
                  <input
                    type="text"
                    value={bankCharge}
                    onChange={(e) => setBankCharge(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bank Name</label>
                  <input
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cheque No.</label>
                  <input
                    type="text"
                    value={chequeNo}
                    onChange={(e) => setChequeNo(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cheque Date</label>
                  <input
                    type="date"
                    value={chequeDt}
                    onChange={(e) => setChequeDt(e.target.value)}
                    style={styles.input}
                  />
                </div>
              </>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                style={styles.textarea}
              />
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionHeading}>📊 Summary</h2>
          <div style={styles.summaryContainer}>
            <div style={styles.summaryBox}>
              <div style={styles.summaryRow}>
                <span>Original Amount</span>
                <input
                  type="number"
                  value={originalNetTotal.toFixed(2)}
                  readOnly
                  style={styles.summaryInput}
                />
              </div>
              <div style={styles.summaryRow}>
                <span>Current Outstanding</span>
                <input
                  type="number"
                  value={currentOutstanding.toFixed(2)}
                  readOnly
                  style={styles.summaryInput}
                />
              </div>
              <div style={styles.summaryRow}>
                <span>Payment Amount</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={currentOutstanding}
                  value={paymentAmountInput}
                  onChange={(e) => setPaymentAmountInput(e.target.value)}
                  style={{ ...styles.summaryInput, backgroundColor: '#fff' }}
                  placeholder="0.00"
                />
              </div>
              <div style={styles.summaryTotal}>
                <span>Remaining Outstanding</span>
                <span>₹{netAmountInput}</span>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.actionButtons}>
          <button
            onClick={handleSubmit}
            style={styles.saveButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
            }}
          >
            ✅ {transactionToEdit ? 'Update' : 'Save'}
          </button>
          <button
            onClick={handleCancel}
            style={styles.cancelButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
            }}
          >
            ❌ Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayableForm;